import { useMemo } from 'react';
import { useQuery } from 'urql';

import { graphql } from '../../__generated__';
import {
  CostInsightsEdge,
  CostInsightsQuery,
  ResourceNodeType,
} from '../../__generated__/graphql';
import { CostInsight } from '../../metadata-shared';

// Export the GraphQL query type for consumers who need to mock this query
export type { CostInsightsQuery };

const CostInsightsDocument = graphql(/* GraphQL */ `
  query CostInsights(
    $environmentId: BigInt!
    $filter: CostInsightsFilter
    $first: Int
  ) {
    environment(id: $environmentId) {
      costInsights(filter: $filter, first: $first) {
        totalCount
        edges {
          node {
            executionComputeUnits
            executionComputeUnitsSaved
            executionCost
            executionCostSaved
            executionCount
            executionTime
            executionTimeSaved
            reusedCount
            runDate
            isCostProcessed
          }
        }
      }
    }
  }
`);

const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a date-only string ("yyyy-MM-dd") into a Date that represents *local midnight*.
 *
 * Why: `new Date("yyyy-MM-dd")` (and many UTC-based parsers) can produce an
 * off-by-one-day display when later formatted in the user's local timezone.
 */
export const parseDateString = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/**
 * Parse the cost insights edge with the date parsed as a local date
 * @param edge - The cost insights edge
 * @returns The cost insight with the date parsed as a local date
 */
export const parseCostInsight = (
  edge: Omit<CostInsightsEdge, 'cursor'> | undefined,
): CostInsight | undefined => {
  const node = edge?.node;
  if (!node) return undefined;

  // early exit if the run date is not in the correct format
  if (!YYYY_MM_DD_REGEX.test(node.runDate)) return undefined;

  return {
    date: parseDateString(node.runDate),
    executionCount: node.executionCount ?? 0,
    reusedCount: node.reusedCount ?? 0,
    executionTime: node.executionTime ?? 0,
    executionComputeUnits: node.executionComputeUnits ?? 0,
    executionCost: node.executionCost ?? 0,
    executionTimeSaved: node.executionTimeSaved ?? 0,
    executionComputeUnitsSaved: node.executionComputeUnitsSaved ?? 0,
    executionCostSaved: node.executionCostSaved ?? 0,
    isCostProcessed: node.isCostProcessed ?? true,
  };
};

/**
 * Hook to fetch cost insights data for a given environment, model, or job definition
 * @example
 * const { data, isLoading, isError, isFetching } = useCostInsights({
 *   environmentId: 1,
 *   uniqueId: 'model_1',
 *   jobDefinitionId: 1,
 *   pause: false,
 * });
 *
 * console.log(data);
 * @param environmentId - The environment ID
 * @param uniqueId - The unique ID of the model or test
 * @param jobDefinitionId - The job definition ID
 * @param resourceTypes - Filter by resource types (e.g. [ResourceNodeType.Test])
 * @param pause - Whether to pause the query
 * @param parser - The parser function to parse the cost insights data
 * @returns The cost insights data
 */
export const useCostInsights = ({
  environmentId,
  uniqueId,
  jobDefinitionId,
  resourceTypes,
  pause,
  parser = parseCostInsight,
}: {
  environmentId: number;
  uniqueId?: string;
  jobDefinitionId?: number;
  resourceTypes?: ResourceNodeType[] | null;
  pause?: boolean;
  parser?: (
    edge: Omit<CostInsightsEdge, 'cursor'> | undefined,
  ) => CostInsight | undefined;
}) => {
  const [queryState] = useQuery({
    query: CostInsightsDocument,
    variables: {
      environmentId,
      filter: {
        uniqueId: uniqueId ?? null,
        jobDefinitionId: jobDefinitionId ?? null,
        lookbackDays: 365,
        resourceTypes: resourceTypes ?? null,
      },
      first: 365,
    },
    pause: pause || !environmentId,
  });

  const data = useMemo(
    () =>
      queryState.data?.environment?.costInsights?.edges
        ?.map(parser)
        ?.filter((data) => data !== undefined),
    [queryState.data, parser],
  );

  return {
    data,
    isLoading: queryState.fetching,
    isError: queryState.error,
    isFetching: queryState.fetching,
  };
};
